/**
 * Configuración de breadcrumbs para todas las rutas del frontend.
 *
 * Cada nodo:
 *   - `match`: patrón de pathname con soporte de `:param` (matching manual, no react-router).
 *   - `label`: string estático o `(params, dynamicLabels) => string`.
 *   - `parent`: patrón del nodo padre (para construir la cadena hacia arriba).
 *   - `root`: si `true`, no se renderiza breadcrumb cuando el pathname corresponde a este nodo.
 *
 * Labels dinámicos: las páginas de detalle pueden registrar el label real
 * con `useSetBreadcrumbLabel('client:' + id, cliente?.name)` — hasta que
 * llega, el label muestra `#<id>` como fallback.
 */

export const breadcrumbNodes = [
  // ─── EJECUTIVO (/admin/*) ──────────────────────────────────────────────
  { match: '/admin', label: 'Inicio', root: true },
  { match: '/admin/panel-operacional', label: 'Panel Operacional', parent: '/admin' },
  { match: '/admin/hallazgos', label: 'Hallazgos', parent: '/admin' },
  { match: '/admin/tecnicos', label: 'Técnicos', parent: '/admin' },
  { match: '/admin/users', label: 'Usuarios', parent: '/admin' },
  {
    match: '/admin/administradores',
    label: 'Administradores de Edificios',
    parent: '/admin',
  },
  { match: '/admin/client', label: 'Clientes', parent: '/admin' },
  {
    match: '/admin/clientes/:id',
    label: (p, dyn) => dyn['client:' + p.id] ?? `Cliente #${p.id}`,
    parent: '/admin/client',
  },
  { match: '/admin/tenants', label: 'Tenants', parent: '/admin' },
  {
    match: '/admin/tenants/:tenantId/edit',
    label: (p, dyn) => `Editar ${dyn['tenant:' + p.tenantId] ?? '#' + p.tenantId}`,
    parent: '/admin/tenants',
  },
  {
    match: '/admin/tenants/:tenantId/pdf-templates',
    label: 'Plantillas PDF',
    parent: '/admin/tenants',
  },
  {
    match: '/admin/tenants/:tenantId/pdf-templates/:documentType',
    label: (p) => `Plantilla: ${p.documentType}`,
    parent: '/admin/tenants/:tenantId/pdf-templates',
  },
  {
    match: '/admin/tenants/:tenantId/menu-items',
    label: 'Menú Items',
    parent: '/admin/tenants',
  },
  {
    match: '/admin/tenants/:tenantId/garantias',
    label: 'Garantías',
    parent: '/admin/tenants',
  },
  { match: '/admin/folders', label: 'Archivos', parent: '/admin' },
  { match: '/admin/budget', label: 'Presupuestos', parent: '/admin' },
  {
    match: '/admin/document-composer/:id/:tipoDocumento',
    label: (p) => `Composer: ${p.tipoDocumento}`,
    parent: '/admin/budget',
  },
  { match: '/admin/certificacion', label: 'Certificaciones', parent: '/admin' },
  {
    match: '/admin/certificacion/nueva',
    label: 'Nueva certificación',
    parent: '/admin/certificacion',
  },
  {
    match: '/admin/certificacion/:id',
    label: (p, dyn) => dyn['cert:' + p.id] ?? `Certificación #${p.id}`,
    parent: '/admin/certificacion',
  },
  { match: '/admin/colecciones', label: 'Colecciones', parent: '/admin' },
  {
    match: '/admin/colecciones/:id',
    label: (p, dyn) => dyn['coleccion:' + p.id] ?? `Colección #${p.id}`,
    parent: '/admin/colecciones',
  },
  {
    match: '/admin/colecciones/:id/items/:itemId',
    label: (p, dyn) => dyn['item:' + p.itemId] ?? `Ítem #${p.itemId}`,
    parent: '/admin/colecciones/:id',
  },
  { match: '/admin/maintance', label: 'Mantenciones (master)', parent: '/admin' },
  { match: '/admin/mantenciones', label: 'Mantenciones', parent: '/admin' },
  {
    match: '/admin/mantenciones/calendario',
    label: 'Calendario',
    parent: '/admin/mantenciones',
  },
  {
    match: '/admin/mantenciones/actividades',
    label: 'Actividades',
    parent: '/admin/mantenciones',
  },
  {
    match: '/admin/mantenciones/:id',
    label: (p, dyn) => dyn['mantencion:' + p.id] ?? `Mantención #${p.id}`,
    parent: '/admin/mantenciones',
  },
  { match: '/admin/maintance/asignations', label: 'Asignaciones', parent: '/admin/maintance' },
  { match: '/admin/maintance/categories', label: 'Categorías', parent: '/admin/maintance' },
  { match: '/admin/maintanceReports', label: 'Reportes', parent: '/admin' },
  { match: '/admin/documentos', label: 'Documentos', parent: '/admin' },

  // ─── SPECIALIST (/specialist/*) ────────────────────────────────────────
  { match: '/specialist', label: 'Inicio', root: true },
  {
    match: '/specialist/AttentionVoucher',
    label: 'Comprobante de atención',
    parent: '/specialist',
  },
  {
    match: '/specialist/lista-mantenciones',
    label: 'Lista de mantenciones',
    parent: '/specialist',
  },
  {
    match: '/specialist/lista-mantenciones/:idMantencion',
    label: (p, dyn) => dyn['mantencion:' + p.idMantencion] ?? `Mantención #${p.idMantencion}`,
    parent: '/specialist/lista-mantenciones',
  },
  { match: '/specialist/order', label: 'Órdenes', parent: '/specialist' },
  {
    match: '/specialist/order/:id',
    label: (p, dyn) => dyn['order:' + p.id] ?? `Orden #${p.id}`,
    parent: '/specialist/order',
  },
  {
    match: '/specialist/order/:id/finish-form',
    label: 'Finalizar orden',
    parent: '/specialist/order/:id',
  },
  { match: '/specialist/maintanceReports', label: 'Reportes', parent: '/specialist' },
  {
    match: '/specialist/maintanceReports/:id',
    label: (p, dyn) => dyn['report:' + p.id] ?? `Reporte #${p.id}`,
    parent: '/specialist/maintanceReports',
  },
  {
    match: '/specialist/maintanceReports/:id/finish-form',
    label: 'Finalizar reporte',
    parent: '/specialist/maintanceReports/:id',
  },
  {
    match: '/specialist/mantenciones/:id',
    label: (p, dyn) => dyn['mantencion:' + p.id] ?? `Mantención #${p.id}`,
    parent: '/specialist',
  },

  // ─── LIFTLOGIC (/ raíz del super-tenant) ───────────────────────────────
  { match: '/', label: 'Inicio', root: true },
  { match: '/constructorPdf', label: 'Constructor PDF', parent: '/' },
  {
    match: '/tenants/:id',
    label: (p, dyn) => dyn['tenant:' + p.id] ?? `Tenant #${p.id}`,
    parent: '/',
  },
  { match: '/tenants/:id/usuarios', label: 'Usuarios', parent: '/tenants/:id' },
  { match: '/administrador', label: 'Administrador', parent: '/' },
  {
    match: '/pdf-templates/:documentType',
    label: (p) => `Plantilla: ${p.documentType}`,
    parent: '/',
  },
  { match: '/liftlogic/menu-items', label: 'Menú Items', parent: '/' },
  { match: '/liftlogic/support-settings', label: 'Configuración de soporte', parent: '/' },
  { match: '/liftlogic/incidents', label: 'Reportes de incidencia', parent: '/' },
];
